import {
	IIntrospect,
	ILoginResponse,
	IUserFormData,
	UserWithoutID,
	User,
	IRegisterFormData,
	ISetPassswordRequestBody,
	PaginatedUser,
	IUpdateUserTypePayload,
} from "@/types/UserTypes";
import axios, { AxiosInstance } from "axios";

export class AuthClient {
	private client: AxiosInstance;

	/**
	 * @param baseURL base url of the api
	 * @param version version of the api (e.g. v1)
	 */
	constructor(baseURL: string, version: string) {
		this.client = axios.create({
			baseURL: baseURL + "/" + version,
		});
	}

	setToken(token: string) {
		this.client.defaults.headers.common["Authorization"] =
			"Bearer " + token.trim();
	}

	// Auth
	getIntrospect = async (): Promise<IIntrospect> => {
		try {
			const { data } = await this.client.post<IIntrospect>(
				`/auth/introspective`,
				null
			);
			return data;
		} catch (error: any) {
			// Log the error for debugging
			console.error('Introspect API error:', error.response?.data || error.message);
			throw error;
		}
	};

	signIn = async (loginForm: IUserFormData): Promise<ILoginResponse> => {
		const { data } = await this.client.post<ILoginResponse>(
			`/auth/login`,
			loginForm
		);
		return data;
	};

	signUp = async (signupForm: IRegisterFormData) => {
		const { data } = await this.client.post(
			`/auth/register`,
			signupForm
		);
		return data;
	};

	forgotPassword = async (email: string): Promise<number> => {
		return this.client.post(
			`/auth/forgot_password`,
			{ email }
		).then((res: any) => res.status)
		.catch((err: any) => err.response.status);
	};

	setPassword = async (setPasswordForm: ISetPassswordRequestBody) => {
		const { data } = await this.client.post(
			`/auth/set_password`,
			setPasswordForm
		);
		return data;
	};

	changePassword = async (oldPassword: string, newPassword: string) => {
		const { data } = await this.client.post(
			`/auth/change_password`,
			{
				old_password: oldPassword,
				new_password: newPassword,
			}
		);
		return data;
	};

	updateOwnUser = async (user: UserWithoutID): Promise<User> => {
		const { data } = await this.client.patch<User>(`/users/own`, user);
		return data;
	};

	getOwnUser = async (): Promise<User> => {
		const { data } = await this.client.get<User>(`/users/own`);
		return data;
	};

	getUser = async (id: number): Promise<User> => {
		const { data } = await this.client.get<User>(`/users/${id}`);
		return data;
	};

	signInWithOAuth = async (token: string, provider: string, is_for_signup: boolean, user_type: number | null): Promise<ILoginResponse> => {
		const { data } = await this.client.post<ILoginResponse>(
			`/auth/oauth2/login`,
			{ token, provider, user_type, is_for_signup }
		);
		return data;
	};

	validateResetID = async (resetId: string): Promise<void> => {
		const { data } = await this.client.get<void>(`/auth/reset_id/${resetId}`);
		return data;
	};

	uploadUserImage = async (fileUrl: string, keyId: string): Promise<User | null> => {
		return this.client
			.post(`/users/image`, {image_url: fileUrl, key_id: keyId})
			.then((res: any) => res.data)
			.catch((_) => {
				return null;
			});
	};

	getUsers = async (query: string = ""): Promise<PaginatedUser | null> => {
		try {
			const { data } = await this.client.get<PaginatedUser>(`/users/?${query}`);
			return data;
		} catch (error: any) {
			return null;
		}
	};

	updateUserType = async (userType: IUpdateUserTypePayload) => {
		const { data } = await this.client.patch<User>(`/users/type`, userType);
		return data;
	};
}
